import React, { useMemo } from "react";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend } from "recharts";

type Props = {
    title?: string;
    periodLabel: string;
    data: { name: string; value: number; id?: string }[];
    loading?: boolean;
    error?: string | null;
    colors?: string[];
    height?: number;
    className?: string;
    onSectorClick?: (item: any) => void;
};

const ProviderDonutComponent: React.FC<Props> = ({
    title = "Distribución por tipo", 
    periodLabel,
    data = [],
    loading = false,
    error = null,
    colors = ["#6366f1", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6"],
    height = 280,
    className = "",
    onSectorClick,
}) => {
    const noData = useMemo(() => data.length === 0 || data.every(d => d.value === 0), [data]);

    return (
        <div className={`rounded-2xl border bg-white shadow-sm ${className}`}>
            <div className="p-4 flex items-center justify-between">
                <h3 className="font-semibold">{title}</h3>
                <div className="text-sm text-gray-600">{periodLabel}</div>
            </div>

            <div className="px-4 pb-6">
                {loading ? (
                    <div className="p-6 text-sm text-gray-500">Cargando estadísticas…</div>
                ) : error ? (
                    <div className="p-6 text-sm text-rose-600">{error}</div>
                ) : (
                    <div className="relative" style={{ height }}>
                        {noData && (
                            <div className="absolute inset-0 z-10 flex items-center justify-center">
                                <div className="rounded-xl bg-white/80 px-4 py-2 text-sm text-gray-700 shadow">
                                    No hay datos para el periodo seleccionado.
                                </div>
                            </div>
                        )}

                        <div className={noData ? "h-full opacity-40 blur-[2px] pointer-events-none" : "h-full"}>
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                <Pie
                                    data={data}
                                    dataKey="value"
                                    nameKey="name"
                                    innerRadius={70}
                                    outerRadius={110}
                                    paddingAngle={2}
                                    cornerRadius={3}
                                    onClick={onSectorClick}
                                >
                                    {data.map((_, i) => (
                                        <Cell key={i} fill={colors[i % colors.length]} cursor="pointer" />
                                    ))}
                                </Pie>
                                    <Tooltip formatter={(value: any, name: any) => [value, name]} />
                                    <Legend
                                        verticalAlign="bottom"
                                        height={24}
                                        content={(props: any) => {
                                            const { payload } = props; // cada entry tiene value (nombre), color, etc.

                                            return (
                                                <ul className="flex flex-wrap justify-center gap-3 text-s">
                                                    {payload.map((entry: any, index: number) => {
                                                        const item = data.find(d => d.name === entry.value);

                                                        return (
                                                            <li
                                                                key={index}
                                                                className="flex items-center gap-1 cursor-pointer"
                                                                onClick={() => item && onSectorClick?.(item)}
                                                            >
                                                                <span
                                                                    className="inline-block h-2 w-2 rounded-full"
                                                                    style={{ backgroundColor: entry.color }}
                                                                />
                                                                <span className="uppercase">{entry.value}</span>
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            );
                                        }}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export const ProviderDonut = React.memo(ProviderDonutComponent);
